import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Stack, Typography } from '@mui/material';
import { DatePicker } from '@mui/lab';
import moment from 'moment';

import { StyledTextField } from '../../../../../components/Inputs';
import InputLabel from '../../../../../components/v2/InputLabel';
import ToggleButtonGroup from '../../../../components/ToggleButtonGroup';
import ChipMultiSelect from '../../components/ChipMultiSelect';
import CountryConsentFields from '../../components/CountryConsentFields';
import SectionCard from '../../components/SectionCard';
import { Field } from '../../components/style';
import { AUDIENCE_OPTIONS, RECIPIENT_GROUP_OPTIONS, isClientAudience } from '../../utils/constants';
import texts from '../../texts.json';
import { DateField } from './style';

const renderDateInput = (params) => (
  <DateField>
    <StyledTextField {...params} />
  </DateField>
);

const AudienceSection = ({
  audience,
  onAudienceChange,
  onAudienceTypeChange,
  onToggleSalesStage,
  onToggleCountry,
  onRequireConsentChange,
}) => {
  const isClients = isClientAudience(audience.audienceType);

  const handleStartChange = (next) => {
    onAudienceChange({ startDate: next ? moment(next).startOf('day') : null });
  };
  const handleEndChange = (next) => {
    onAudienceChange({ endDate: next ? moment(next).endOf('day') : null });
  };

  return (
    <SectionCard title={texts.sections.audience.title}>
      <Field>
        <InputLabel>{texts.sections.audience.recipientGroup}</InputLabel>
        <ToggleButtonGroup
          value={audience.audienceType}
          options={RECIPIENT_GROUP_OPTIONS}
          onChange={onAudienceTypeChange}
        />
      </Field>

      {isClients ? (
        <Alert severity="warning" sx={{ mt: 1 }}>
          {texts.sections.audience.consentWarning}
        </Alert>
      ) : (
        <ChipMultiSelect
          label={texts.audienceFields.leadState}
          options={AUDIENCE_OPTIONS}
          selected={audience.salesStages}
          onToggle={onToggleSalesStage}
          helperText={texts.sections.audience.leadStateHelper}
        />
      )}

      <Field>
        <InputLabel>
          {isClients ? texts.sections.audience.cancellationDateRange : texts.sections.audience.dateRange}
        </InputLabel>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <DatePicker
            value={audience.startDate}
            onChange={handleStartChange}
            maxDate={audience.endDate}
            renderInput={renderDateInput}
          />
          <Typography variant="body2" color="text.secondary">
            {texts.sections.audience.dateRangeSeparator}
          </Typography>
          <DatePicker
            value={audience.endDate}
            onChange={handleEndChange}
            minDate={audience.startDate}
            renderInput={renderDateInput}
          />
        </Stack>
      </Field>

      <CountryConsentFields
        countries={audience.countries}
        requireConsent={audience.requireConsent}
        onToggleCountry={onToggleCountry}
        onRequireConsentChange={onRequireConsentChange}
        countryLabel={texts.audienceFields.country}
        consentLabel={texts.audienceFields.consentLabel}
        showConsent={!isClients}
      />
    </SectionCard>
  );
};

AudienceSection.propTypes = {
  audience: PropTypes.shape({
    audienceType: PropTypes.string,
    startDate: PropTypes.object,
    endDate: PropTypes.object,
    countries: PropTypes.arrayOf(PropTypes.string),
    salesStages: PropTypes.arrayOf(PropTypes.string),
    requireConsent: PropTypes.bool,
  }).isRequired,
  onAudienceChange: PropTypes.func.isRequired,
  onAudienceTypeChange: PropTypes.func.isRequired,
  onToggleSalesStage: PropTypes.func.isRequired,
  onToggleCountry: PropTypes.func.isRequired,
  onRequireConsentChange: PropTypes.func.isRequired,
};

export default AudienceSection;
